import { configureStore, SerializedError } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { api, apiServerDownCheck } from "_shared/_stores/api";
import { rootReducer } from "_shared/_stores/root-reducer";
import { assistantTurnsAblyReduxMiddleware } from "ai-assistant/_stores/assistant-turns-ably-redux-middleware";
import { documentsAblyReduxMiddleware } from "documents/_stores/documents-ably-redux-middleware";
import { entityMemberInvitationsAblyReduxMiddleware } from "legal-entities/invitations/member-invitations/_stores/entity-member-invitations-ably-redux-middleware";
import { messagesAblyReduxMiddleware } from "messaging/_stores/_realtime/messages-ably-redux-middleware";
import { marketplaceSearchCacheHydrationMiddleware } from "marketplace/_stores/marketplace-search-cache-hydration-middleware";
import { entityNotificationsAblyReduxMiddleware } from "notifications/entity-summaries/_stores/_realtime/entity-notifications-ably-redux-middleware";
import { notificationsAblyReduxMiddleware } from "notifications/_stores/_realtime/notifications-ably-redux-middleware";
import { permissionsAblyReduxMiddleware } from "permissions/_stores/permissions-ably-redux-middleware";
import {
  networkConnectionsAblyReduxMiddleware,
  networkInvitationsAblyReduxMiddleware,
} from "users/networking/_stores/networking-ably-redux-middleware";
import {
  loadProposalLocalStoreState,
  proposalLocalStorePersistenceMiddleware,
} from "work/proposals/_store/proposal-local-store";
import { proposalsAblyReduxMiddleware } from "work/proposals/_store/proposals-ably-redux-middleware";
import { hydrateLocalProposalStore } from "work/proposals/_store/proposals-redux-slice";
import { proposalCollaborationPersistenceMiddleware } from "work/proposals/negotiation/proposal-collaboration-local-store";
import { commentsAblyReduxMiddleware } from "work/proposals/comments/_store/realtime/comments-ably-redux-middleware";
import { teamTemplatesAblyReduxMiddleware } from "work/team-templates/_store/team-templates-ably-redux-middleware";
import { workAgreementsAblyReduxMiddleware } from "work/work-agreements/_store/work-agreements-ably-redux-middleware";
import { systemMessagesAblyMiddleware } from "_shared/_stores/realtime/system-messages-ably-middleware";
import { principalChangeReduxMiddleware } from "users/__session/principal-change-redux-middleware";
import { IS_DEV, IS_E2E } from "_shared/_utilities/meta";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "./store-types";
export type { RootState } from "_shared/_stores/root-reducer";

const attorneyHubStore = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false })
      .concat(messagesAblyReduxMiddleware)
      .concat(notificationsAblyReduxMiddleware)
      .concat(entityNotificationsAblyReduxMiddleware)
      .concat(permissionsAblyReduxMiddleware)
      .concat(networkConnectionsAblyReduxMiddleware)
      .concat(networkInvitationsAblyReduxMiddleware)
      .concat(proposalsAblyReduxMiddleware)
      .concat(workAgreementsAblyReduxMiddleware)
      .concat(systemMessagesAblyMiddleware)
      .concat(teamTemplatesAblyReduxMiddleware)
      .concat(commentsAblyReduxMiddleware)
      .concat(documentsAblyReduxMiddleware)
      .concat(entityMemberInvitationsAblyReduxMiddleware)
      .concat(assistantTurnsAblyReduxMiddleware)
      .concat(marketplaceSearchCacheHydrationMiddleware)
      .concat(proposalLocalStorePersistenceMiddleware)
      .concat(proposalCollaborationPersistenceMiddleware)
      .concat(principalChangeReduxMiddleware)
      .concat(apiServerDownCheck)
      .concat(api.middleware),
});

// The one place that lets a query ask again when the window regains focus or comes back into view,
// for the queries that opt in with `refetchOnFocus` (the table column preferences, #6151). No query
// sets `refetchOnReconnect`, so that half changes nothing.
setupListeners(attorneyHubStore.dispatch);

const proposalLocalStoreState = loadProposalLocalStoreState();
if (proposalLocalStoreState) {
  attorneyHubStore.dispatch(hydrateLocalProposalStore(proposalLocalStoreState));
}

/**
 * A read-only handle on the store, in development and functional-test builds, for diagnosing a stuck page.
 *
 * A page that renders a loader forever is the hardest failure this app produces to diagnose from the
 * outside: every gate spells it the same way (`if (!thing || isLoading) return <Loader/>`), so the
 * symptom names neither which flag is stuck nor which of several queries owns it. Functional tests
 * could previously only report "a progressbar is still visible", which cost several real-stack runs
 * spent guessing at causes that the evidence then contradicted.
 *
 * Deliberately not a dispatch handle: this exists so a harness can ASK what the state is, never so it
 * can arrange one. A test that sets up through the store stops testing the app that ships.
 */
if (IS_DEV || IS_E2E) {
  Object.defineProperty(globalThis, "__ATTORNEY_HUB_STORE_STATE__", {
    configurable: true,
    value: () => attorneyHubStore.getState(),
    writable: false,
  });
}

export interface StoreState<T> {
  entries: Record<string, T>;
  loading: Record<string, boolean>;
  error: Record<string, SerializedError | null>;
}

export const useAttorneyHubDispatch = () => useDispatch<AppDispatch>();
export default attorneyHubStore;
